import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import paymentsApi from '../../services/paymentsApi';
import BatchStatusChip from '../../components/payments/BatchStatusChip';
import { useAuth } from '../../context/AuthContext';

const STATUS_OPTIONS = [
  '', 'imported', 'operations_review', 'draft', 'submitted_to_finance',
  'finance_review', 'returned_to_operations', 'finance_approved', 'closed',
];
const money = (v) => (v == null ? '$0.00' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function BatchList() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [invoiceWeek, setInvoiceWeek] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (invoiceWeek) params.invoice_week = invoiceWeek;
      setBatches(await paymentsApi.listBatches(params));
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [status, invoiceWeek]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { field: 'batch_number', headerName: 'Batch', width: 150 },
    {
      field: 'status', headerName: 'Status', width: 190,
      renderCell: (p) => <BatchStatusChip status={p.value} />,
    },
    { field: 'invoice_week', headerName: 'Invoice week', width: 130 },
    { field: 'order_source', headerName: 'Source', width: 130 },
    { field: 'total_rows', headerName: 'Rows', width: 80, type: 'number' },
    { field: 'payable_rows', headerName: 'Payable', width: 90, type: 'number' },
    { field: 'flagged_rows', headerName: 'Flagged', width: 90, type: 'number' },
    { field: 'total_providers', headerName: 'Providers', width: 95, type: 'number' },
    {
      field: 'total_calculated_amount', headerName: 'Calculated', width: 130, type: 'number',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'uploaded_at', headerName: 'Uploaded', width: 180,
      valueFormatter: (v) => (v ? new Date(v).toLocaleString() : ''),
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Vendor Payment Batches</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasRole('operations') && (
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => navigate('/payments/upload')}
              >
                Upload New Batch
              </Button>
            )}
            <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s || 'all'} value={s}>{s ? s.replace(/_/g, ' ') : 'All statuses'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Invoice week"
            placeholder="YYYY-MM-DD"
            value={invoiceWeek}
            onChange={(e) => setInvoiceWeek(e.target.value)}
          />
        </Box>

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={batches}
            columns={columns}
            loading={loading}
            getRowId={(r) => r.id}
            density="compact"
            onRowClick={(p) => navigate(`/payments/batches/${p.id}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default BatchList;
